/**
 * Compliance Report Builder
 * =========================
 *
 * Assembles a structured compliance report from real PureSustain data sources.
 * Every section reads from a real API; missing sources are marked with
 * `available: false` so the renderer can show a clean empty state instead of
 * synthetic values.
 *
 * Used by:
 *   - Compliance page (preview + JSON/PDF download)
 *   - Public verification page (subset of fields)
 */

import { jsPDF } from 'jspdf';

const stripUndefined = (obj) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined));

export const buildComplianceReport = ({
  batch,
  events,
  monitoring_sessions,
  alarms,
  corrections,
  generated_at,
}) => {
  const hasBatch = !!batch;
  const hasEvents = Array.isArray(events) && events.length > 0;
  const hasMonitoring = Array.isArray(monitoring_sessions) && monitoring_sessions.length > 0;
  const hasAlarms = Array.isArray(alarms) && alarms.length > 0;
  const hasCorrections = Array.isArray(corrections) && corrections.length > 0;
  const hasCompliance =
    Array.isArray(batch?.compliance) && batch.compliance.length > 0;

  const totalReadings =
    Array.isArray(monitoring_sessions)
      ? monitoring_sessions.reduce((s, m) => s + (m.reading_count || 0), 0)
      : 0;
  const warningCount = Array.isArray(alarms) ? alarms.filter((a) => a.status === 'warning').length : 0;
  const criticalCount = Array.isArray(alarms) ? alarms.filter((a) => a.status === 'critical').length : 0;

  return {
    meta: stripUndefined({
      generated_at: generated_at || new Date().toISOString(),
      generated_by: 'PureSustain Compliance Report Builder',
      platform: 'PureSustain — Aquaculture Transparency Platform',
      owner: 'PureSustain AS',
      disclaimer:
        'MVP demonstration environment. Data may include test, estimated, simulated, or live sensor data depending on deployment configuration.',
    }),

    batch_identity: {
      available: hasBatch,
      batch_id: batch?.id || null,
      batch_number: batch?.batch_number || null,
      product: batch?.product_name || null,
      species: batch?.species || null,
      origin_name: batch?.origin?.name || null,
      origin_region: batch?.origin?.region || null,
      catch_or_harvest_location: batch?.origin?.name || null,
      supplier: batch?.supplier || null,
      production_date: batch?.catch_date || null,
      production_timestamp: batch?.catch_date || null,
      current_location: batch?.current_location || null,
      current_status: batch?.current_status || null,
    },

    traceability_summary: {
      available: hasEvents,
      traceability_status: hasEvents ? 'Recorded' : 'Pending records',
      traceability_completeness:
        hasEvents ? `${events.length} event(s) recorded` : null,
      chain_of_custody_status: hasEvents ? 'In progress' : 'Pending records',
      verification_status:
        hasEvents && !hasCorrections ? 'Verified' :
        hasCorrections ? 'Verified with manual corrections' :
        'Pending records',
    },

    chain_of_custody_summary: {
      available: hasEvents || hasCorrections,
      stages_completed: hasEvents ? events.length : 0,
      missing_stages: null, // computed in UI from canonical stage list
      manual_corrections_count: hasCorrections ? corrections.length : 0,
      traceability_gaps: null, // computed in UI
      verification_outcome:
        hasCorrections ? 'Verified with manual corrections'
          : hasEvents ? 'In progress'
          : 'Pending records',
    },

    cold_chain_summary: {
      available: hasMonitoring || hasAlarms,
      total_sensor_readings: totalReadings,
      total_monitoring_sessions: hasMonitoring ? monitoring_sessions.length : 0,
      threshold_breaches: warningCount + criticalCount,
      warning_events: warningCount,
      critical_events: criticalCount,
      alarm_summary: hasAlarms
        ? `${warningCount} warning, ${criticalCount} critical alarm(s)`
        : null,
    },

    sustainability_summary: {
      available: hasBatch && batch?.total_co2_kg != null,
      sustainability_status: hasBatch ? 'Recorded' : 'Pending records',
      total_co2_kg: hasBatch ? batch?.total_co2_kg : null,
      environmental_indicators: null,
      sustainability_notes: null,
    },

    compliance_summary: {
      available: hasCompliance,
      documentation_completeness: hasCompliance ? 'Complete' : 'Pending records',
      audit_readiness:
        hasCompliance && !hasCorrections ? 'Audit-ready'
          : hasCorrections ? 'Audit-ready with manual corrections'
          : 'Pending records',
      outstanding_actions: hasCorrections ? 0 : null,
      compliance_observations: hasCompliance ? batch.compliance : [],
    },

    manual_corrections: {
      available: hasCorrections,
      heading: 'Verified Operator Resolutions',
      description:
        'Resolved by authorized operator with full audit trail. PureSustain records, '
        + 'resolves, and verifies operational exceptions transparently — corrections are '
        + 'evidence of governance, not failure.',
      total_resolutions: hasCorrections ? corrections.length : 0,
      live_vs_corrected: {
        live_events: hasEvents ? events.length : 0,
        manually_resolved: hasCorrections ? corrections.length : 0,
      },
      entries: hasCorrections
        ? corrections.map((c) =>
            stripUndefined({
              correction_id: c.id,
              missing_stage: c.missing_stage_label,
              reason: c.reason,
              corrective_action: c.corrective_action,
              evidence_label: c.evidence_label,
              resolved_by: c.user,
              resolved_by_role: c.user_role,
              timestamp: c.timestamp,
              status: c.status,
            }),
          )
        : [],
    },

    corrective_actions: {
      available: hasCorrections,
      traceability_gaps_count: null, // computed live
      manual_corrections: hasCorrections
        ? corrections.map((c) =>
            stripUndefined({
              correction_id: c.id,
              timestamp: c.timestamp,
              user: c.user,
              missing_stage: c.missing_stage_label,
              previous_stage: c.previous_stage,
              next_stage: c.next_stage,
              reason: c.reason,
              corrective_action: c.corrective_action,
              evidence_label: c.evidence_label,
              status: c.status,
            }),
          )
        : [],
    },

    audit_trail_summary: {
      available: hasEvents || hasCorrections || hasAlarms,
      total_audit_events:
        (hasEvents ? events.length : 0) +
        (hasCorrections ? corrections.length : 0) +
        (hasAlarms ? alarms.length : 0),
      manual_corrections_count: hasCorrections ? corrections.length : 0,
      verification_actions: hasCorrections ? corrections.length : 0,
      system_generated_events:
        (hasEvents ? events.length : 0) + (hasAlarms ? alarms.length : 0),
    },
  };
};

/* ----------------------- Downloads ----------------------- */

export const downloadJSON = (report, filename = 'compliance-report.json') => {
  const blob = new Blob([JSON.stringify(report, null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const downloadPDF = (report, filename = 'compliance-report.pdf') => {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();
  let y = 56;

  const line = (text, opts = {}) => {
    const { size = 10, bold = false, color = [15, 23, 42], indent = 0 } = opts;
    if (y > 780) { doc.addPage(); y = 56; }
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setFontSize(size);
    doc.setTextColor(...color);
    const wrapped = doc.splitTextToSize(String(text ?? '—'), W - 80 - indent);
    doc.text(wrapped, 40 + indent, y);
    y += wrapped.length * (size + 4);
  };

  const section = (title) => {
    y += 8;
    if (y > 760) { doc.addPage(); y = 56; }
    doc.setDrawColor(226, 232, 240);
    doc.line(40, y, W - 40, y);
    y += 14;
    line(title, { size: 13, bold: true, color: [15, 118, 110] });
    y += 2;
  };

  const kv = (k, v) => {
    if (v === null || v === undefined || v === '') {
      line(`${k}: —`, { color: [100, 116, 139] });
    } else {
      line(`${k}: ${v}`);
    }
  };

  // Header
  doc.setFillColor(15, 118, 110);
  doc.rect(0, 0, W, 40, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('PureSustain — Compliance Report', 40, 26);
  doc.setTextColor(15, 23, 42);

  line(`Generated: ${new Date(report.meta.generated_at).toLocaleString()}`, { color: [100, 116, 139] });
  line('A product of PureSustain AS — Aquaculture Transparency Platform', { color: [100, 116, 139] });

  section('Batch Identity');
  if (!report.batch_identity.available) {
    line('No batch records available yet.', { color: [100, 116, 139] });
  } else {
    kv('Batch ID', report.batch_identity.batch_id);
    kv('Batch Number', report.batch_identity.batch_number);
    kv('Product', report.batch_identity.product);
    kv('Species', report.batch_identity.species);
    kv('Origin', report.batch_identity.origin_name);
    kv('Origin Region', report.batch_identity.origin_region);
    kv('Catch/Harvest Location', report.batch_identity.catch_or_harvest_location);
    kv('Supplier', report.batch_identity.supplier);
    kv('Production Date', report.batch_identity.production_date);
    kv('Production Timestamp', report.batch_identity.production_timestamp);
  }

  section('Traceability Summary');
  if (!report.traceability_summary.available) line('No traceability events recorded yet.', { color: [100, 116, 139] });
  else {
    kv('Traceability Status', report.traceability_summary.traceability_status);
    kv('Completeness', report.traceability_summary.traceability_completeness);
    kv('Chain-of-Custody Status', report.traceability_summary.chain_of_custody_status);
    kv('Verification Status', report.traceability_summary.verification_status);
  }

  section('Chain of Custody Summary');
  const coc = report.chain_of_custody_summary;
  if (!coc.available) line('No chain-of-custody events recorded yet.', { color: [100, 116, 139] });
  else {
    kv('Stages Completed', coc.stages_completed);
    kv('Manual Corrections', coc.manual_corrections_count);
    kv('Verification Outcome', coc.verification_outcome);
  }

  section('Cold Chain Summary');
  const cc = report.cold_chain_summary;
  if (!cc.available) line('No cold-chain activity recorded yet.', { color: [100, 116, 139] });
  else {
    kv('Sensor Readings', cc.total_sensor_readings);
    kv('Monitoring Sessions', cc.total_monitoring_sessions);
    kv('Threshold Breaches', cc.threshold_breaches);
    kv('Warning Events', cc.warning_events);
    kv('Critical Events', cc.critical_events);
    kv('Alarm Summary', cc.alarm_summary);
  }

  section('Sustainability Summary');
  if (!report.sustainability_summary.available) line('No sustainability records available yet.', { color: [100, 116, 139] });
  else {
    kv('Status', report.sustainability_summary.sustainability_status);
    kv('Total CO₂ (kg)', report.sustainability_summary.total_co2_kg);
  }

  section('Compliance Summary');
  if (!report.compliance_summary.available) line('No compliance events recorded yet.', { color: [100, 116, 139] });
  else {
    kv('Documentation Completeness', report.compliance_summary.documentation_completeness);
    kv('Audit Readiness', report.compliance_summary.audit_readiness);
    kv('Outstanding Actions', report.compliance_summary.outstanding_actions);
  }

  section('Corrective Actions');
  const ca = report.corrective_actions;
  if (!ca.available) line('No corrective actions logged.', { color: [100, 116, 139] });
  else {
    ca.manual_corrections.forEach((mc, idx) => {
      line(`#${idx + 1} ${mc.missing_stage} (${mc.correction_id})`, { bold: true });
      kv('Reason', mc.reason);
      kv('Corrective Action', mc.corrective_action);
      kv('Operator', mc.user);
      kv('Timestamp', mc.timestamp);
      kv('Status', mc.status);
    });
  }

  section('Audit Trail Summary');
  const at = report.audit_trail_summary;
  if (!at.available) line('No audit events recorded yet.', { color: [100, 116, 139] });
  else {
    kv('Total Audit Events', at.total_audit_events);
    kv('Manual Corrections', at.manual_corrections_count);
    kv('Verification Actions', at.verification_actions);
    kv('System-Generated Events', at.system_generated_events);
  }

  // Footer
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text(
      `PureSustain AS · ${report.meta.disclaimer}`,
      40,
      doc.internal.pageSize.getHeight() - 24,
      { maxWidth: W - 80 },
    );
    doc.text(`Page ${i} / ${pageCount}`, W - 80, doc.internal.pageSize.getHeight() - 24);
  }

  doc.save(filename);
};
